import { useState, useEffect, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { 
    getMessaging, 
    getToken, 
    onMessage, 
    isSupported,
    deleteToken
} from 'firebase/messaging';
import { notificationService as apiNotificationService } from '../services/apiService';

const firebaseConfig = {
    apiKey: "AIzaSyBCE9Hd2YKQn-xgEsObxq_y9eMMWH8HvE8",
    authDomain: "trongduan-16f32.firebaseapp.com",
    projectId: "trongduan-16f32",
    storageBucket: "trongduan-16f32.firebasestorage.app",
    messagingSenderId: "655745416209",
    appId: "1:655745416209:web:c60a7cbec954b0c6f105b0",
    measurementId: "G-5W1R2GBPXY"
};

const vapidKey = "BFxGATCXYofQaL3gYjd4GOlu0bZkCdKmZzWSJIAGhAaDRlkm96wFijjfq0VXM0no9yO6CKq6iFrCFNWGs6upJAo";

export const useFirebaseMessaging = (userData) => {
    const [messagingToken, setMessagingToken] = useState('');
    const [permission, setPermission] = useState('default');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastMessage, setLastMessage] = useState(null);
    const messagingRef = useRef(null);
    const tokenRegistered = useRef(false);

    // Initialize Firebase
    useEffect(() => {
        const initFirebase = async () => {
            try {
                // Check if messaging is supported
                const supported = await isSupported();
                if (!supported) {
                    setError('Firebase Messaging không được hỗ trợ trên thiết bị này');
                    return;
                }

                // Initialize Firebase app
                const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
                
                // Initialize messaging
                const messaging = getMessaging(app);
                messagingRef.current = messaging;

                // Check current permission
                setPermission(Notification.permission);

                // Listen for foreground messages
                const unsubscribe = onMessage(messaging, (payload) => {
                    console.log('Foreground message received:', payload);
                    setLastMessage(payload);
                    
                    // Show notification even when app is in foreground
                    if (payload.notification) {
                        showForegroundNotification(payload);
                    }
                });

                return unsubscribe;
            } catch (err) {
                console.error('Firebase initialization error:', err);
                setError('Lỗi khởi tạo Firebase: ' + err.message);
            }
        };

        initFirebase();
    }, []);

    // Auto-register FCM token when user data is available
    useEffect(() => {
        const autoRegisterToken = async () => {
            if (userData && !tokenRegistered.current && messagingRef.current) {
                console.log('Auto-registering FCM token for user:', userData);
                
                // Check permission first
                if (Notification.permission === 'granted') {
                    await getMessagingToken();
                } else if (Notification.permission === 'default') {
                    // Request permission automatically
                    const granted = await requestPermission();
                    if (granted) {
                        await getMessagingToken();
                    }
                }
            }
        };

        autoRegisterToken();
    }, [userData, messagingRef.current]);

    // Request notification permission
    const requestPermission = async () => {
        if (!('Notification' in window)) {
            setError('Trình duyệt không hỗ trợ thông báo');
            return false;
        }

        setIsLoading(true);
        setError(null);

        try {
            const permission = await Notification.requestPermission();
            setPermission(permission);

            if (permission === 'granted') {
                await getMessagingToken();
                return true;
            } else {
                setError('Người dùng từ chối quyền thông báo');
                return false;
            }
        } catch (err) {
            console.error('Permission request error:', err);
            setError('Lỗi yêu cầu quyền thông báo: ' + err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // Get FCM token
    const getMessagingToken = async () => {
        if (!messagingRef.current) {
            throw new Error('Messaging chưa được khởi tạo');
        }

        try {
            const token = await getToken(messagingRef.current, { vapidKey });
            console.log('FCM Token:', token);
            setMessagingToken(token);

            // Send token to server
            if (userData && token && !tokenRegistered.current) {
                await sendTokenToServer(token, userData);
                tokenRegistered.current = true;
            }

            return token;
        } catch (err) {
            console.error('Error getting FCM token:', err);
            throw new Error('Không thể lấy token: ' + err.message);
        }
    };

    // Send token to server
    const sendTokenToServer = async (token, user) => {
        try {
            // Get the driver ID from user data
            const driverId = user.idLaiXe ;
            
            if (!driverId) {
                console.warn('Driver ID not found in user data, cannot register FCM token');
                return;
            }
            
            console.log('Registering FCM token with server for driver:', driverId);
            
            // Register FCM token with the server
            const result = await apiNotificationService.registerFCMToken(driverId, token);
            
            if (result.success) {
                console.log('✅ FCM token registered successfully with server');
            } else {
                console.warn('❌ Failed to register FCM token with server:', result.message);
                setError('Lỗi đăng ký token với server: ' + result.message);
            }
        } catch (err) {
            console.error('Error registering FCM token with server:', err);
            setError('Lỗi kết nối server: ' + err.message);
        }
    };

    // Delete token (for logout)
    const deleteMessagingToken = async () => {
        if (!messagingRef.current || !messagingToken) return;

        try {
            await deleteToken(messagingRef.current);
            setMessagingToken('');
            tokenRegistered.current = false;
            console.log('FCM token deleted');
        } catch (err) {
            console.error('Error deleting FCM token:', err);
        }
    };

    // Show notification when app is in foreground
    const showForegroundNotification = (payload) => {
        if ('serviceWorker' in navigator && 'showNotification' in ServiceWorkerRegistration.prototype) {
            navigator.serviceWorker.ready.then(registration => {
                const title = payload.notification?.title || 'Thông báo mới';
                const options = {
                    body: payload.notification?.body || 'Có thông báo mới',
                    icon: payload.notification?.icon || '/icon.png',
                    badge: '/badge.png',
                    tag: payload.data?.tag || 'foreground',
                    data: payload.data || {},
                    requireInteraction: false,
                    silent: false
                };

                registration.showNotification(title, options);
            });
        }
    };

    // Test notification
    const testNotification = () => {
        if (permission === 'granted') {
            const testPayload = {
                notification: {
                    title: 'Thông báo thử nghiệm',
                    body: 'Hệ thống thông báo đang hoạt động bình thường',
                    icon: '/icon.png'
                },
                data: {
                    url: '/',
                    type: 'test'
                }
            };
            showForegroundNotification(testPayload);
        }
    };

    return {
        messagingToken,
        permission,
        isLoading,
        error,
        lastMessage,
        requestPermission,
        getMessagingToken,
        deleteMessagingToken,
        testNotification,
        isSupported: !!messagingRef.current
    };
};